<template>
    <div>
        <div v-if="!files.length" class="report-media-empty">
            <i class="fas fa-camera"></i>
            <span>Фото по отчёту не найдены</span>
        </div>
        <div v-else class="report-media-grid">
            <button
                v-for="(file, index) in normalizedFiles"
                :key="file.key"
                type="button"
                class="report-media-tile"
                :class="{'report-media-tile--video': file.isVideo, 'report-media-tile--file': !file.isImage && !file.isVideo}"
                @click="openViewer(index)"
            >
                <img v-if="file.isImage" :src="file.thumbnailUrl" :alt="file.name" loading="lazy">
                <span v-else class="report-media-file-preview">
                    <i :class="file.iconClass"></i>
                    <span v-if="file.isVideo" class="report-media-play">
                        <i class="fas fa-play"></i>
                    </span>
                    <small>{{ file.formatLabel }}</small>
                </span>
                <span v-if="showStatus" class="report-media-status" :class="`report-media-status--${file.statusVariant}`">
                    {{ file.statusText }}
                </span>
            </button>
        </div>
    </div>
</template>

<script>
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'avi', 'webm', 'mkv', '3gp'];

export default {
    name: 'ReportMediaGrid',
    props: {
        showStatus: {type: Boolean, default: true},
        files: {
            type: Array,
            default: () => []
        },
        reportId: {
            type: [Number, String],
            default: null
        },
        annulled: {
            type: Boolean,
            default: false
        },
        annulmentReason: {
            type: String,
            default: ''
        }
    },
    computed: {
        authQuery() {
            const token = this.$store && this.$store.state ? this.$store.state.token : null;
            return token ? `?gjuid=${encodeURIComponent(token)}` : '';
        },
        normalizedFiles() {
            return (this.files || []).map((item, index) => this.normalizeFile(item, index));
        },
        viewerFiles() {
            return this.normalizedFiles.map(file => ({
                id: file.id,
                key: file.key,
                name: file.name,
                url: file.url,
                downloadUrl: file.url,
                thumbnailUrl: file.thumbnailUrl,
                mimeType: file.mimeType,
                isImage: file.isImage,
                isVideo: file.isVideo,
                galleryShiftId: this.reportId,
                galleryShiftTitle: `Отчёт №${this.reportId}`,
                reportAnnulled: this.annulled,
                reportAnnulmentReason: this.annulmentReason,
                raw: file.raw
            }));
        }
    },
    methods: {
        normalizeFile(item, index) {
            const id = item && item.id ? item.id : index;
            const name = item && (item.name || item.filename) ? (item.name || item.filename) : `Файл ${id}`;
            const mimeType = String(item && item.mimeType ? item.mimeType : '').toLowerCase();
            const extension = this.getExtension(name);
            const isVideo = Boolean(item && item.isVideo) ||
                mimeType.indexOf('video/') === 0 ||
                VIDEO_EXTENSIONS.includes(extension);
            const isImage = !isVideo && (
                Boolean(item && item.isImage) ||
                mimeType.indexOf('image/') === 0 ||
                /\.(png|jpe?g|jfif|gif|webp|bmp|heic|heif)$/i.test(name)
            );
            const statusMeta = this.getStatusMeta(item && item.status);
            return {
                id,
                key: `${this.reportId || 'report'}_${id}_${index}`,
                name,
                mimeType,
                isImage,
                isVideo,
                url: `${item.url || '/images/' + id}${this.authQuery}`,
                thumbnailUrl: isImage ? `${item.thumbnailUrl || '/images/mini/' + id}${this.authQuery}` : null,
                iconClass: isVideo ? 'fas fa-file-video' : 'fas fa-file',
                formatLabel: isVideo ? 'VIDEO' : (extension ? extension.toUpperCase() : 'FILE'),
                statusText: statusMeta.text,
                statusVariant: statusMeta.variant,
                raw: item
            };
        },
        getExtension(name) {
            const match = String(name || '').toLowerCase().match(/\.([a-z0-9]{1,8})$/);
            return match ? match[1] : '';
        },
        getStatusMeta(status) {
            if (status === 'reuse_pending') return {text: 'Ожидает переименования', variant: 'warning'};
            if (status === 'sent') {
                return {text: 'Доставлено', variant: 'success'};
            }
            if (status === 'processed') {
                return {text: 'Ожидает системной отправки', variant: 'warning'};
            }
            if (status === 'pending' || status === 'temp') {
                return {text: 'Обрабатывается', variant: 'info'};
            }
            if (status === 'telegram_failed') {
                return {text: 'Сбой доставки', variant: 'danger'};
            }
            if (status === 'shift_not_found') {
                return {text: 'Ошибка привязки', variant: 'danger'};
            }
            return {text: status || 'Статус не указан', variant: 'secondary'};
        },
        openViewer(index) {
            if (this.$fileViewer) {
                this.$fileViewer.open(this.viewerFiles, {
                    startIndex: index,
                    source: 'reports',
                    continuous: true
                });
                return;
            }
            window.open(this.normalizedFiles[index].url, '_blank');
        }
    }
}
</script>

<style scoped>
.report-media-empty {
    align-items: center;
    border: 1px dashed #cfd6df;
    border-radius: 6px;
    color: #6c757d;
    display: flex;
    gap: 8px;
    justify-content: center;
    min-height: 86px;
}

.report-media-grid {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fill, minmax(138px, 1fr));
}

.report-media-tile {
    aspect-ratio: 1 / 1;
    background: #f8f9fa;
    border: 0;
    border-radius: 6px;
    cursor: pointer;
    overflow: hidden;
    padding: 0;
    position: relative;
}

.report-media-tile img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
}

.report-media-file-preview {
    align-items: center;
    background: #25313d;
    color: #fff;
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;
    justify-content: center;
    width: 100%;
}

.report-media-file-preview > i {
    font-size: 34px;
}

.report-media-play {
    align-items: center;
    background: rgba(255, 255, 255, .92);
    border-radius: 999px;
    color: #17a2b8;
    display: flex;
    height: 42px;
    justify-content: center;
    padding-left: 3px;
    position: absolute;
    width: 42px;
}

.report-media-status {
    border-radius: 4px;
    bottom: 6px;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    left: 6px;
    line-height: 1.2;
    padding: 3px 5px;
    position: absolute;
    right: 6px;
}

.report-media-status--success { background: rgba(40, 167, 69, .92); }
.report-media-status--warning { background: rgba(255, 193, 7, .95); color: #212529; }
.report-media-status--info { background: rgba(23, 162, 184, .94); }
.report-media-status--danger { background: rgba(220, 53, 69, .94); }
.report-media-status--secondary { background: rgba(108, 117, 125, .94); }

@media (max-width: 767.98px) {
    .report-media-grid {
        gap: 6px;
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .report-media-tile {
        border-radius: 5px;
    }

    .report-media-status {
        bottom: 4px;
        display: -webkit-box;
        font-size: 9px;
        left: 4px;
        max-height: 26px;
        overflow: hidden;
        padding: 2px 4px;
        right: 4px;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
    }
}

@media (max-width: 380px) {
    .report-media-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
